import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, Alert, Typography, Divider,
} from "@mui/material";
import {
  createBillingItem, saveReceptor, fmtCbteFecha,
  Receptor, BillingItem, CbteFechaStatus,
} from "../services/billing";
import ReceptorFields, {
  ReceptorFormValue, receptorFormFrom, receptorPayload, receptorEmailsInvalidos,
} from "./ReceptorFields";

interface Props {
  open:        boolean;
  period:      string;                     // 'YYYYMMDD-YYYYMMDD' (mes seleccionado)
  monthLabel:  string;                     // "01/07/2026 → 31/07/2026"
  receptors:   Record<string, Receptor>;
  /** Estado de la fecha del comprobante del período (null si no se pudo leer). */
  cbteFecha?:  CbteFechaStatus | null;
  onClose:     () => void;
  onCreated:   (item: BillingItem, receptor?: Receptor) => void;
}

// Mismo saneo que el backend: el collector es sort key y viaja en URLs. NO se
// fuerza mayúscula: los colectores son CASE-SENSITIVE (sushi2x1 ≠ SUSHI2X1), así
// que hay que respetar lo que escribe el operador.
const normalizeCollector = (s: string) =>
  s.replace(/[^A-Za-z0-9]/g, "").slice(0, 40);

/**
 * Alta de una factura manual — cliente/monto que NO vienen en el archivo del
 * proceso upstream. Crea la fila PENDIENTE en invoice_ar y guarda el receptor
 * con los datos del formulario. Después la fila sigue el flujo normal: emitir.
 * La empresa emisora la determina la BD (collection_config), no la pestaña — la
 * fila puede aparecer en la otra.
 *
 * Los campos del receptor son los MISMOS que el editor del lápiz (componente
 * `ReceptorFields`): un alta manual suele ser un cliente nuevo, y antes había
 * que crear la fila y después ir a completarlo aparte.
 */
export default function NewInvoiceDialog({ open, period, monthLabel, receptors, cbteFecha, onClose, onCreated }: Props) {
  const [cuit, setCuit]           = useState("");
  const [collector, setCollector] = useState("");
  const [amount, setAmount]       = useState("");
  const [form, setForm]           = useState<ReceptorFormValue>(receptorFormFrom(null));
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCuit(""); setCollector(""); setAmount("");
      setForm(receptorFormFrom(null)); setErr(null);
    }
  }, [open]);

  const cuitClean   = cuit.replace(/\D/g, "");
  const cuitValid   = /^\d{11}$/.test(cuitClean);
  const existing    = receptors[cuitClean];

  // Si el CUIT ya tiene receptor cargado, se precarga: el PUT reescribe el item
  // completo, así que arrancar vacío borraría lo que ya estaba.
  useEffect(() => {
    if (open && cuitValid) setForm(receptorFormFrom(existing ?? null));
  }, [open, cuitValid, cuitClean]);   // eslint-disable-line react-hooks/exhaustive-deps

  const parsed      = Number(amount.replace(",", "."));
  const amountValid = !isNaN(parsed) && parsed > 0;
  const net         = amountValid ? parsed / 1.21 : 0;
  const tax         = amountValid ? parsed - net : 0;
  const emailsInvalidos = receptorEmailsInvalidos(form);
  const valid = cuitValid && collector.length > 0 && amountValid && !emailsInvalidos.length;

  async function onSave() {
    if (!valid) return;
    setBusy(true); setErr(null);
    try {
      const item = await createBillingItem({
        period, collector, cuit: cuitClean, amount: parsed,
      });
      // El receptor va después de crear la fila: si el alta la rechaza (gate de
      // facturación estándar), no queremos haber tocado el receptor igual.
      // Best-effort: si falla, la fila queda y se completa desde el lápiz.
      let receptor: Receptor | undefined;
      if (form.razonSocial.trim()) {
        try { receptor = await saveReceptor(cuitClean, receptorPayload(form)); }
        catch { /* editable después */ }
      }
      onCreated(item, receptor);
      onClose();
    } catch (e: any) {
      setErr(e.message ?? "No se pudo crear la factura");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Nueva factura manual
        <Typography variant="body2" color="text.secondary">
          Período {monthLabel}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {/* La factura manual usa la fecha RETROACTIVA del batch (último día del
              período) mientras el período tenga estándar sin emitir Y no haya vencido
              la ventana de 10 días; cumplida alguna de las dos → fecha actual.
              Ver _resolve_cbte_fch(manual=True) + _period_pending_standard en el backend. */}
          {cbteFecha && (() => {
            const enVentana = cbteFecha.days_late != null && cbteFecha.days_late <= cbteFecha.retro_max_days;
            const manualRetro = enVentana && cbteFecha.pending > 0;
            return (
              <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  {manualRetro ? (
                    <>Fecha del comprobante: <b>{fmtCbteFecha(cbteFecha.period_end)}</b> (retroactiva
                      al cierre del período). Las facturas manuales usan la fecha retroactiva del batch
                      hasta que se emitan todas las pendientes del período o pasen los primeros
                      {" "}{cbteFecha.retro_max_days} días del mes siguiente.</>
                  ) : (
                    <>Fecha del comprobante: <b>{fmtCbteFecha(cbteFecha.today)}</b> (actual).</>
                  )}
                </Typography>
              </Alert>
            );
          })()}

          <TextField
            label="CUIT receptor"
            value={cuit}
            onChange={(e) => setCuit(e.target.value)}
            error={cuit.length > 0 && !cuitValid}
            helperText={cuit.length > 0 && !cuitValid ? "11 dígitos" : " "}
            disabled={busy}
            fullWidth
            autoFocus
          />
          {existing?.razon_social && (
            <Alert severity="info" sx={{ py: 0 }}>
              Receptor ya cargado — los datos se precargaron y se guardan con lo que dejes acá.
            </Alert>
          )}

          <TextField
            label="Collector (identificador único)"
            value={collector}
            onChange={(e) => setCollector(normalizeCollector(e.target.value))}
            helperText="Letras y números, sin espacios — respeta mayúsculas/minúsculas (es la clave de la fila)"
            disabled={busy}
            fullWidth
          />
          <TextField
            label="Total c/IVA (ARS)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={amount.length > 0 && !amountValid}
            helperText={amountValid
              ? `Neto ${net.toFixed(2)}  ·  IVA 21% ${tax.toFixed(2)}`
              : "Monto mayor a 0"}
            disabled={busy}
            fullWidth
          />

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">Datos del receptor</Typography>
          </Divider>

          <ReceptorFields
            cuit={cuitClean}
            value={form}
            onChange={setForm}
            disabled={busy}
            resetKey={open}
            // El padrón también sirve para nombrar la fila si todavía no tiene
            // collector: es lo primero que haría el operador a mano.
            onLookup={(r) => {
              if (!collector && r.razon_social) setCollector(normalizeCollector(r.razon_social));
            }}
          />

          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button onClick={onSave} variant="contained" disabled={busy || !valid}>
          {busy ? "Creando…" : "Crear factura"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
